import { ALL_SDK_NETWORKS, MINIMUM_POSITIONS_VAULT_TVL } from '@/config/query/queryParams'
import { DEFAULT_SETTINGS } from '@/context/settings'
import type { VaultApyRange } from '@/types/apy'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

export const positionsQueryKey = ['positions'] as const

interface PositionsQueryParams {
  address: Address | undefined
  apyInterval?: VaultApyRange
}

function positionsQueryOptions({ address, apyInterval = DEFAULT_SETTINGS.apyRange }: PositionsQueryParams) {
  return queryOptions({
    queryKey: [...positionsQueryKey, { userAddress: address, apyInterval }] as const,
    async queryFn({ queryKey }) {
      const { userAddress } = queryKey[1]
      if (!userAddress) return null
      const defiSdk = getDefiSdk()
      return await defiSdk.getPositions({
        path: { userAddress },
        query: {
          apyInterval,
          allowedNetworks: ALL_SDK_NETWORKS,
          minTvl: MINIMUM_POSITIONS_VAULT_TVL,
        },
      })
    },
    select(data) {
      if (data == null) {
        return null
      }

      return data.data.map(
        ({
          vaultId,
          address,
          network,
          name,
          protocol,
          childrenVaults,
          asset,
          lpToken,
          apy,
          additionalAssets,
          apyComposite,
        }) => {
          // NOTE: Currently, we're skipping additional assets unclaimed native values since we don't have a way to denominate them in the main asset.
          const childrenBalanceUsd =
            childrenVaults?.reduce((acc, child) => acc + Number(child.lpToken?.balanceUsd), 0) ?? 0
          const childrenBalanceNative =
            childrenVaults?.reduce((acc, child) => acc + BigInt(child.asset?.positionValueInAsset ?? 0), 0n) ?? 0n
          const childrenUnclaimedUsd =
            childrenVaults?.reduce(
              (acc, child) =>
                acc +
                Number(child.asset?.unclaimedUsd ?? 0) +
                (child.additionalAssets?.reduce((acc, asset) => acc + Number(asset.unclaimedUsd ?? 0), 0) ?? 0),
              0
            ) ?? 0
          const childrenUnclaimedNative =
            childrenVaults?.reduce((acc, child) => acc + BigInt(child.asset?.unclaimedNative ?? 0), 0n) ?? 0n
          const unclaimedAssetsUsd =
            Number(asset.unclaimedUsd ?? 0) +
            (additionalAssets
              ?.map(({ unclaimedUsd }) => Number(unclaimedUsd))
              .reduce((acc, val) => acc + Number(val ?? 0), 0) ?? 0)
          const positionValueUsd =
            Number(lpToken?.balanceUsd) + unclaimedAssetsUsd + childrenBalanceUsd + childrenUnclaimedUsd
          const positionValueNative =
            BigInt(asset?.positionValueInAsset ?? 0) +
            BigInt(asset.unclaimedNative ?? 0) +
            childrenBalanceNative +
            childrenUnclaimedNative

          return {
            vaultId,
            address,
            network,
            name,
            protocol,
            asset,
            apy,
            balanceNative: positionValueNative.toString(),
            balanceUsd: positionValueUsd,
            childrenVaults,
            apyComposite,
          }
        }
      )
    },
    enabled: !!address,
    retry: 2,
    staleTime: 1 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
    placeholderData: undefined,
  })
}

export const usePositionsQuery = ({ address, apyInterval }: PositionsQueryParams) => {
  const { data, isFetching } = useQuery({
    ...positionsQueryOptions({ address, apyInterval }),
  })
  return { data, isFetching }
}

export type PositionsList = ReturnType<typeof usePositionsQuery>['data']
